/**
 * Raster Catalog — fetches and indexes available raster layers from R2.
 *
 * Provides keyword-based lookup for the voice command pipeline.
 * Gracefully degrades if catalog is unavailable (no layers = no raster commands).
 */
import { RASTER_CATALOG_URL } from '../config.js';

// ── State ──────────────────────────────────────────────────────────
let catalog = null;       // Raw catalog JSON
let keywordIndex = null;  // Map<keyword, layer[]>
let refreshTimer = null;

// Keyword aliases — maps spoken terms to canonical raster type
const ALIASES = {
  vegetation: 'ndvi',
  greenness: 'ndvi',
  'vegetation index': 'ndvi',
  'plant health': 'ndvi',
  'land cover': 'landcover',
  'land use': 'landcover',
  lulc: 'landcover',
  elevation: 'dem',
  height: 'dem',
  terrain: 'dem',
  'digital elevation': 'dem',
  'true color': 'rgb',
  'natural color': 'rgb',
  satellite: 'rgb',
  imagery: 'rgb',
  'false color': 'false_color',
  infrared: 'false_color',
  moisture: 'ndmi',
  'water index': 'ndwi',
  water: 'ndwi',
  'built-up': 'ndbi',
  urban: 'ndbi',
  temperature: 'lst',
  'surface temperature': 'lst',
  thermal: 'lst',
};

/**
 * Initialize the raster catalog. Call once at app startup.
 * Fetches the catalog JSON from R2 and builds the keyword index.
 */
export async function initRasterCatalog() {
  try {
    await fetchCatalog();
    // Auto-refresh every 5 minutes
    refreshTimer = setInterval(fetchCatalog, 5 * 60 * 1000);
    console.log('[RasterCatalog] Initialized', catalog?.layers?.length || 0, 'layers');
  } catch (err) {
    console.warn('[RasterCatalog] Init failed (raster commands will be unavailable):', err.message);
  }
}

async function fetchCatalog() {
  try {
    // Cache-bust: r2.dev edge-caches the catalog object, so a freshly ingested
    // layer can be invisible for many minutes. A per-fetch query param forces a
    // cache miss so newly-deployed raster layers show up promptly.
    const resp = await fetch(`${RASTER_CATALOG_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!resp.ok) {
      throw new Error(`HTTP ${resp.status}`);
    }
    catalog = await resp.json();
    buildIndex();
  } catch (err) {
    // Don't overwrite existing catalog on refresh failure
    if (!catalog) {
      catalog = { version: '0', layers: [] };
      keywordIndex = new Map();
    }
    console.warn('[RasterCatalog] Fetch failed:', err.message);
  }
}

function buildIndex() {
  keywordIndex = new Map();
  if (!catalog?.layers) return;

  for (const layer of catalog.layers) {
    const type = layer.type?.toLowerCase();
    if (!type) continue;

    // Index by canonical type
    addToIndex(type, layer);

    // Index by aliases that point to this type
    for (const [alias, target] of Object.entries(ALIASES)) {
      if (target === type) {
        addToIndex(alias, layer);
      }
    }

    // Index by layer title words (for location-based search)
    if (layer.title) {
      const words = layer.title.toLowerCase().split(/\s+/);
      for (const word of words) {
        if (word.length > 3) addToIndex(word, layer);
      }
    }
  }
}

function addToIndex(keyword, layer) {
  const existing = keywordIndex.get(keyword);
  if (existing) {
    existing.push(layer);
  } else {
    keywordIndex.set(keyword, [layer]);
  }
}

/**
 * Look up a raster layer by type keyword, optionally filtered by location.
 * @param {string} keyword - Raster type (e.g., 'ndvi', 'vegetation', 'land cover')
 * @param {string} [location] - Optional location filter (e.g., 'Nairobi')
 * @returns {object|null} The best matching layer, or null
 */
export function getRasterLayer(keyword, location) {
  if (!keywordIndex) return null;

  // Normalize keyword via aliases
  const normalized = ALIASES[keyword?.toLowerCase()] || keyword?.toLowerCase();
  const layers = keywordIndex.get(normalized);

  if (!layers || layers.length === 0) return null;

  // The viz path (render_raster) renders PMTiles/WEBP tiles. Analytic COG
  // layers (format: 'cog', e.g. DEMs) are read directly by GeoLibre, not the
  // tile renderer — exclude them here so an analytic layer never reaches
  // render_raster. Entries without an explicit format are legacy PMTiles.
  const vizLayers = layers.filter(l => (l.format || 'pmtiles') !== 'cog');
  if (vizLayers.length === 0) return null;

  // If location specified, try to match
  if (location) {
    const locLower = location.toLowerCase();
    const locationMatch = vizLayers.find(l =>
      l.location?.toLowerCase().includes(locLower) ||
      l.title?.toLowerCase().includes(locLower)
    );
    if (locationMatch) return locationMatch;
  }

  // Return the most recent layer of this type
  return vizLayers[0];
}

/**
 * Look up an ANALYTIC raster layer (format: 'cog', e.g. a DEM) by type keyword,
 * optionally filtered by location. These are real-valued Cloud-Optimized
 * GeoTIFFs read directly (HTTP range requests) by the GeoLibre raster tools —
 * NOT rendered as WEBP tiles. Returned layer carries { url, bounds, band_stats,
 * crs, resolution_m }. (Phase 2 terrain-analysis op consumes this.)
 * @param {string} keyword - e.g. 'dem', 'elevation', 'terrain'
 * @param {string} [location]
 * @returns {object|null}
 */
export function getAnalyticRasterLayer(keyword, location) {
  if (!keywordIndex) return null;

  const normalized = ALIASES[keyword?.toLowerCase()] || keyword?.toLowerCase();
  const layers = keywordIndex.get(normalized);
  if (!layers || layers.length === 0) return null;

  // Corrupt-bounds layers (see hasValidAnalyticBounds) are unusable: the
  // renderer cannot place them and the mask cannot align. Skip them so the
  // caller falls through to the on-demand builder.
  const cogs = layers.filter(l => l.format === 'cog' && hasValidAnalyticBounds(l));
  if (cogs.length === 0) return null;

  if (location) {
    // A location was requested. Only take the catalog fast-path if a pre-ingested
    // layer actually matches it; otherwise return null so the caller can fetch an
    // on-demand DEM for that place. (Returning cogs[0] here would wrongly render,
    // e.g., the Alps for "slope of Kilimanjaro".)
    //
    // Several layers can match one spoken name — e.g. the original Mont Blanc
    // window AND the whole-range Alps DEM both match "the Alps". A student
    // naming a range means the whole feature, so prefer the LARGEST coverage
    // (same principle as the worker's largest-natural geocoding, CL-217);
    // detail windows still win for their own names ("Mont Blanc").
    const matches = cogs.filter(l => analyticLayerMatchesLocation(l, location));
    if (matches.length === 0) return null;
    return matches.reduce((best, l) => (analyticLayerArea(l) > analyticLayerArea(best) ? l : best));
  }

  // No location specified → fall back to the largest analytic DEM (a stable,
  // meaningful default — the widest view — instead of ingest-order cogs[0]).
  return cogs.reduce((best, l) => (analyticLayerArea(l) > analyticLayerArea(best) ? l : best));
}

/**
 * True when a layer carries a USABLE [w,s,e,n] EPSG:4326 envelope. Pure.
 *
 * Guards against the two corruption modes an antimeridian-wrapping projected
 * grid produces when its envelope is unprojected (CL-227 audit, F2):
 *  - INVERTED bounds (west >= east) — e.g. the LAEA North America COG whose
 *    corners unproject past ±180° gave [151.3, -7.2, -11.3, 79.1]; and
 *  - DEGENERATE near-global longitude spans — the LAEA Asia COG collapsed to
 *    [-180, …, 180, …], which "contains" any bbox in its latitude band and
 *    poisoned getAnalyticLayerCovering (a "DEM of Mexico" clip grabbed Asia).
 * Layers failing this check are ignored by every analytic lookup, so requests
 * fall through to the on-demand builder instead of rendering garbage.
 */
export function hasValidAnalyticBounds(layer) {
  const b = layer?.bounds;
  if (!Array.isArray(b) || b.length !== 4) return false;
  const [w, s, e, n] = b.map(Number);
  if (![w, s, e, n].every(Number.isFinite)) return false;
  if (w >= e || s >= n) return false; // inverted / empty
  if (w < -180 || e > 180 || s < -90 || n > 90) return false; // out of range
  if (e - w >= 359) return false; // degenerate wrap (claims the whole globe)
  return true;
}

/** Geographic coverage of a layer's [w,s,e,n] bounds in square degrees. Pure.
 *  Invalid/corrupt bounds score 0 so they can never win a coverage contest. */
export function analyticLayerArea(layer) {
  if (!hasValidAnalyticBounds(layer)) return 0;
  const b = layer.bounds;
  return Math.abs((Number(b[2]) - Number(b[0])) * (Number(b[3]) - Number(b[1]))) || 0;
}

/** True if layer bounds fully contain bbox [w,s,e,n]. Pure.
 *  A layer with invalid/corrupt bounds contains nothing. */
export function analyticLayerContains(layer, bbox) {
  if (!hasValidAnalyticBounds(layer)) return false;
  const b = layer.bounds;
  if (!Array.isArray(bbox) || bbox.length !== 4) return false;
  return (
    Number(b[0]) <= bbox[0] &&
    Number(b[1]) <= bbox[1] &&
    Number(b[2]) >= bbox[2] &&
    Number(b[3]) >= bbox[3]
  );
}

/**
 * Find the pre-ingested analytic DEM whose bounds fully cover `bbox`, preferring
 * the TIGHTEST (smallest-area) one for best resolution. Lets a boundary-clipped
 * request (e.g. "DEM of Brazil") reuse a pre-built continental/large-area DEM
 * instead of an on-demand build, then clip it to the border. Returns the layer
 * or null.
 * @param {number[]} bbox [w,s,e,n]
 */
export function getAnalyticLayerCovering(bbox) {
  if (!keywordIndex || !Array.isArray(bbox)) return null;
  const seen = new Set();
  const covering = [];
  for (const layers of keywordIndex.values()) {
    for (const l of layers) {
      if (l.format !== 'cog' || seen.has(l.id)) continue;
      seen.add(l.id);
      if (analyticLayerContains(l, bbox)) covering.push(l);
    }
  }
  if (!covering.length) return null;
  return covering.reduce((best, l) => (analyticLayerArea(l) < analyticLayerArea(best) ? l : best));
}

/** Fraction (0–1) of bbox covered by a layer's bounds, in degree² terms. Pure. */
export function analyticLayerOverlapFraction(layer, bbox) {
  if (!hasValidAnalyticBounds(layer) || !Array.isArray(bbox) || bbox.length !== 4) return 0;
  const [w, s, e, n] = bbox;
  const bboxArea = (e - w) * (n - s);
  if (!(bboxArea > 0)) return 0;
  const b = layer.bounds.map(Number);
  const iw = Math.max(w, b[0]);
  const ie = Math.min(e, b[2]);
  const is = Math.max(s, b[1]);
  const inn = Math.min(n, b[3]);
  if (iw >= ie || is >= inn) return 0;
  return ((ie - iw) * (inn - is)) / bboxArea;
}

/**
 * Like getAnalyticLayerCovering, but when no layer FULLY contains bbox, accept
 * the best PARTIAL cover at ≥ minCoverage overlap. Pure lookup.
 *
 * Why: with exact 4326 bounds (CL-227), real countries poke slightly past
 * their continent DEM — the Aleutians pass -168°W (NA DEM's west edge),
 * Kaliningrad sits west of the Asia DEM. Requiring 100% containment would
 * bounce "DEM of the United States" to the 12°-capped on-demand builder (a
 * guaranteed failure); a ≥minCoverage partial cover renders the whole country
 * minus a transparent fringe, which is the far better lesson outcome. The
 * caller knows a partial cover was used via `partial: true`.
 *
 * @param {number[]} bbox [w,s,e,n]
 * @param {number} [minCoverage=0.85]
 * @returns {{layer: object, partial: boolean}|null}
 */
export function getAnalyticLayerCoveringOrBest(bbox, minCoverage = 0.85) {
  const full = getAnalyticLayerCovering(bbox);
  if (full) return { layer: full, partial: false };
  if (!keywordIndex || !Array.isArray(bbox)) return null;

  const seen = new Set();
  let best = null;
  let bestFrac = 0;
  for (const layers of keywordIndex.values()) {
    for (const l of layers) {
      if (l.format !== 'cog' || seen.has(l.id)) continue;
      seen.add(l.id);
      const frac = analyticLayerOverlapFraction(l, bbox);
      // Prefer higher coverage; on a near-tie prefer the finer (smaller) layer.
      if (
        frac > bestFrac + 1e-9 ||
        (Math.abs(frac - bestFrac) <= 1e-9 && best && analyticLayerArea(l) < analyticLayerArea(best))
      ) {
        best = l;
        bestFrac = frac;
      }
    }
  }
  if (!best || bestFrac < minCoverage) return null;
  return { layer: best, partial: true };
}

/** Normalize a spoken location for catalog matching (article/space/punct-insensitive). */
function normalizeSpokenLocation(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^the\s+/, '')
    .replace(/[.,!?]+$/, '')
    .trim();
}

/**
 * Does an analytic catalog layer cover the spoken location? Checks the layer's
 * name, title, and `aliases` (spoken variants the ingest registered, e.g.
 * ["rockies", "rocky mountains"]), article-insensitively and in BOTH inclusion
 * directions — "the rockies" must match a layer aliased "rockies", and
 * "rocky mountains" must match a layer named "Rocky Mountains — whole range".
 * Pure; exported for tests.
 */
export function analyticLayerMatchesLocation(layer, location) {
  const loc = normalizeSpokenLocation(location);
  if (!loc) return false;
  // Word-boundary containment (not raw substring) so an alias like "ural"
  // never matches inside "murals".
  const escape = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const containsWords = (hay, needle) => new RegExp(`\\b${escape(needle)}\\b`).test(hay);
  const candidates = [layer?.name, layer?.title, ...(Array.isArray(layer?.aliases) ? layer.aliases : [])];
  return candidates.some(c => {
    const cand = normalizeSpokenLocation(c);
    if (!cand) return false;
    return containsWords(cand, loc) || containsWords(loc, cand);
  });
}

/**
 * Get all available raster layers.
 * @returns {object[]}
 */
export function getAllRasterLayers() {
  return catalog?.layers || [];
}

/**
 * Get the set of available raster layer type keywords.
 * @returns {string[]}
 */
export function getAvailableLayerTypes() {
  if (!catalog?.layers) return [];
  const types = new Set(catalog.layers.map(l => l.type).filter(Boolean));
  return Array.from(types);
}

/**
 * Check if any raster layers are available.
 * @returns {boolean}
 */
export function hasRasterLayers() {
  return (catalog?.layers?.length || 0) > 0;
}
